import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useResource } from '../../hooks/useResource';
import { SensitiveActionDialog } from '../../shared/actions/SensitiveActionDialog';
import { PageState } from '../../shared/data/PageState';
import { MutationError, toDialogError } from './PartnerFormDialog';
import { toPageError } from './PartnerListPage';
import type { PartnerRepository } from './repository';
import type { IpAllowlistEntry } from './types';

export function IpAllowlistPanel({
  partnerId,
  repository,
}: {
  readonly partnerId: string;
  readonly repository: PartnerRepository;
}) {
  const { t } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [edit, setEdit] = useState<IpAllowlistEntry | 'create' | null>(null);
  const [statusEntry, setStatusEntry] = useState<IpAllowlistEntry | null>(null);
  const resource = useResource(
    (signal) => repository.listIpAllowlist(partnerId, signal),
    [repository, partnerId, refreshKey],
  );
  const refresh = () => setRefreshKey((key) => key + 1);

  return (
    <Stack component="section" spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'space-between' }}
      >
        <div>
          <Typography component="h2" variant="h5">
            {t('partners.ip.title')}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {t('partners.ip.unrestrictedWarning')}
          </Typography>
        </div>
        <Button onClick={() => setEdit('create')} variant="contained">
          {t('partners.ip.add')}
        </Button>
      </Stack>
      <PageState
        emptyMessage={t('partners.ip.empty')}
        error={toPageError(resource.error, t('partners.ip.loadError'))}
        onRetry={refresh}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table aria-label={t('partners.ip.title')} size="small" sx={{ minWidth: 680 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('partners.ip.network')}</TableCell>
                  <TableCell>{t('partners.ip.description')}</TableCell>
                  <TableCell>{t('partners.fields.status')}</TableCell>
                  <TableCell>{t('partners.ip.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resource.data.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.network}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>
                      <Chip
                        color={entry.status === 'ACTIVE' ? 'success' : 'warning'}
                        label={entry.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => setEdit(entry)} size="small">
                        {t('actions.edit')}
                      </Button>
                      <Button color="warning" onClick={() => setStatusEntry(entry)} size="small">
                        {t('partners.actions.changeStatus')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageState>
      <IpEntryDialog
        entry={edit === 'create' ? null : edit}
        mode={edit === 'create' ? 'create' : 'edit'}
        onClose={() => setEdit(null)}
        onSaved={refresh}
        open={edit !== null}
        partnerId={partnerId}
        repository={repository}
      />
      {statusEntry === null ? null : (
        <SensitiveActionDialog
          impact={t('partners.ip.statusImpact', {
            network: statusEntry.network,
            current: statusEntry.status,
            next: statusEntry.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
          })}
          onClose={() => setStatusEntry(null)}
          onConfirm={async ({ reason }) => {
            await repository.changeIpAllowlistStatus(partnerId, statusEntry.id, {
              status: statusEntry.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
              reason,
            });
            refresh();
          }}
          open
          requireEvidence={false}
          showEvidence={false}
          title={t('partners.ip.statusTitle')}
        />
      )}
    </Stack>
  );
}

function IpEntryDialog({
  entry,
  mode,
  onClose,
  onSaved,
  open,
  partnerId,
  repository,
}: {
  readonly entry: IpAllowlistEntry | null;
  readonly mode: 'create' | 'edit';
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
  readonly partnerId: string;
  readonly repository: PartnerRepository;
}) {
  const { t } = useTranslation();
  const submitting = useRef(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<{
    readonly network: string;
    readonly description: string;
    readonly reason: string;
  }>({ defaultValues: { network: '', description: '', reason: '' } });
  useEffect(() => {
    if (open) {
      reset({ network: entry?.network ?? '', description: entry?.description ?? '', reason: '' });
      setError(null);
    }
  }, [entry, open, reset]);
  const submit = handleSubmit(async (values) => {
    if (submitting.current) return;
    submitting.current = true;
    setError(null);
    try {
      const input = {
        network: values.network.trim(),
        description: values.description.trim(),
        reason: values.reason.trim(),
      };
      if (mode === 'create') await repository.createIpAllowlistEntry(partnerId, input);
      else if (entry !== null) await repository.updateIpAllowlistEntry(partnerId, entry.id, input);
      onSaved();
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('partners.ip.mutationError')));
    } finally {
      submitting.current = false;
    }
  });
  return (
    <Dialog fullWidth onClose={() => !submitting.current && onClose()} open={open}>
      <DialogTitle>
        {t(mode === 'create' ? 'partners.ip.createTitle' : 'partners.ip.editTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack component="form" id="ip-entry-form" onSubmit={submit} spacing={2} sx={{ pt: 1 }}>
          {error === null ? null : <MutationError error={error} />}
          <TextField
            disabled={mode === 'edit'}
            error={errors.network !== undefined}
            helperText={errors.network?.message}
            label={t('partners.ip.network')}
            {...register('network', { required: t('partners.ip.networkRequired') })}
          />
          <TextField
            error={errors.description !== undefined}
            helperText={errors.description?.message}
            label={t('partners.ip.description')}
            {...register('description', { required: t('partners.ip.descriptionRequired') })}
          />
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('partners.fields.reason')}
            multiline
            {...register('reason', { required: t('partners.validation.reasonRequired') })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button form="ip-entry-form" type="submit" variant="contained">
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
